import { ApiError } from '../../api/client'
import { describeError } from '../../lib/describeError'
import type { SaveState } from './editorTypes'
import { etDate, etTime } from '../labels'
import { supportsMembership } from '../../lib/brief'
import { decodeEntities } from './blocks'

/** The top bar's words for a save state. Stale gets its own Reload button beside it. */
export function saveStateText(state: SaveState): string {
  switch (state) {
    case 'saved': return 'All changes saved'
    case 'pending':
    case 'saving': return 'Saving…'
    case 'stale': return 'Someone else changed this piece.'
    case 'busy': return 'Busy — retrying…'
    case 'error': return 'Not saved —'
  }
}

/** Plain words for where a source came from. Never the raw id ("M123"). */
export function sourceKindLabel(kind: string, sectionLabel: string): string {
  switch (kind) {
    case 'member': return 'Member article'
    case 'issue': return 'Magazine issue'
    case 'public': return sectionLabel
    case 'web': return 'Web'
    case 'draft': return 'Our draft'
    default: return 'Source'
  }
}

/** A version's original (spec §6.3): the source in its bundle that it was made from. A draft
 *  source is named D{id}; an archive source, or a published draft read from its archive row,
 *  carries that row's article_id. */
export function isOriginalSource(s: { id: string; article_id?: number | null },
                                 madeFrom: { kind: 'draft' | 'article'; id: number; article_id?: number | null } | null | undefined): boolean {
  if (!madeFrom) return false
  if (madeFrom.article_id != null && s.article_id === madeFrom.article_id) return true
  return madeFrom.kind === 'draft' && s.id === `D${madeFrom.id}`
}

export type ClaimFate = 'supported' | 'confirmed' | 'cut' | 'not_found' | 'unsupported'

type Verification = { removed?: string[]; not_found?: string[]; confirmed?: string[] }

/** What became of one checked claim. The editor's own confirmation and the writer's cut win over the check. */
export function claimFate(claim: { text: string; supported: boolean }, v: Verification): ClaimFate {
  if ((v.removed ?? []).includes(claim.text)) return 'cut'
  if ((v.confirmed ?? []).includes(claim.text)) return 'confirmed'
  if (claim.supported) return 'supported'
  if ((v.not_found ?? []).includes(claim.text)) return 'not_found'
  return 'unsupported'
}

export const FATE_TEXT: Record<ClaimFate, string> = {
  supported: 'Supported by the sources',
  confirmed: 'You confirmed',
  cut: 'Cut by the writer',
  not_found: 'Not found in any source. Still in the text.',
  unsupported: 'Not supported by the sources',
}

/** "Member article: Tigercat!" for a supported claim's source id, or null when the id matches nothing. */
export function claimSourceLabel(
  sourceId: string | null | undefined,
  sources: { id: string; kind: string; title: string }[],
  sectionLabel: string,
): string | null {
  if (!sourceId) return null
  const s = sources.find((x) => x.id === sourceId)
  return s ? `${sourceKindLabel(s.kind, sectionLabel)}: ${s.title}` : null
}

export const blockingCount = (items: { blocking: boolean }[]): number => items.filter((i) => i.blocking).length

/** "Oct 3, 9:14 AM ET" */
export const etStamp = (iso: string): string => `${etDate(iso)}, ${etTime(iso)}`

export type HistoryEntry = { what: string; detail: string | null; when: string | null; link?: { label: string; pieceId: number } }

/** Newest first: published, revised (with the earlier version), written; a newer version leads when there is one. */
export function historyEntries(d: {
  created_at: string; requested_by?: string | null; published_at?: string | null
  revised_from_id?: number | null; revision_note?: string | null; superseded_by_id?: number | null
}): HistoryEntry[] {
  const out: HistoryEntry[] = []
  if (d.superseded_by_id != null) {
    out.push({ what: 'Revised again', detail: 'A newer version replaced this one.', when: null, link: { label: 'Open the newer version', pieceId: d.superseded_by_id } })
  }
  if (d.published_at) out.push({ what: 'Published', detail: null, when: etStamp(d.published_at) })
  if (d.revised_from_id != null) {
    out.push({
      what: 'Revised',
      detail: d.revision_note ? `“${d.revision_note}”` : null,
      when: null,
      link: { label: 'Open the earlier version (read only)', pieceId: d.revised_from_id },
    })
  }
  out.push({
    what: d.revised_from_id != null ? 'Rewritten' : 'Written',
    detail: d.requested_by ? `Asked for by ${d.requested_by}` : null,
    when: etStamp(d.created_at),
  })
  return out
}

/** How the page treats a piece: a banner in place of the editor, read-only, or open for edits. */
export type PieceMode = 'check_sources' | 'failed' | 'superseded' | 'locked' | 'editable'

export function pieceMode(status: string): PieceMode {
  if (status === 'sources_ready') return 'check_sources'
  if (status === 'failed') return 'failed'
  if (status === 'superseded') return 'superseded'
  if (status === 'published' || status === 'unpublished') return 'locked'
  return 'editable'
}

/** The "Who will read it" control: titles with membership publishing, before the piece goes out. */
export const showDestination = (status: string, publicationSlug: string): boolean =>
  supportsMembership(publicationSlug) && !['published', 'unpublished', 'superseded'].includes(status)

/** Publish… is offered once an editor has approved the piece (or while it is scheduled). */
export const canOpenPublish = (piece: string | { status: string }): boolean =>
  ['approved', 'queued'].includes(typeof piece === 'string' ? piece : piece.status)

/** Approve is an editor's button on a piece under review (server: editorial.approval). */
export const canApprove = (piece: { status: string }, me: { can_publish?: boolean } | null | undefined): boolean =>
  me?.can_publish === true && ['draft', 'needs_attention'].includes(piece.status)

/** An approved piece can go back to review by an editor. */
export const canUnapprove = (piece: { status: string }, me: { can_publish?: boolean } | null | undefined): boolean =>
  me?.can_publish === true && piece.status === 'approved'

/** Why a writer may not change this piece (server: editorial_scope.WRITER_LOCKED), or null.
 *  A scheduled piece would go out with unreviewed text; live and taken-down pieces are an editor's. */
export const WRITER_QUEUED = 'This piece is scheduled — ask an editor to take it off the schedule first.'
export function writerLockReason(status: string, isWriter: boolean): string | null {
  if (!isWriter) return null
  if (status === 'queued') return WRITER_QUEUED
  if (status === 'approved') return 'An editor approved this piece. Only an editor can change it now.'
  if (status === 'published' || status === 'unpublished') return "This piece is out. Only an editor can change it now."
  return null
}

/** A writer hands a written piece that isn't out yet to an editor (server: editorial.SENDABLE). */
export const canSendToEditor = (status: string): boolean => ['draft', 'needs_attention'].includes(status)

/** A taken-down piece is offered "Put back up" (publish again, the same posts) instead. */
export const canPutBackUp = (status: string): boolean => status === 'unpublished'

/** Set aside frees the subject; a live piece must be taken down first and an earlier version is history. */
export const canSetAside = (status: string): boolean =>
  !['published', 'rejected', 'superseded'].includes(status)

/** Why a write that would strand unsaved edits is refused. */
export const SAVE_FIRST = 'Save your changes first'

/** Why a write is refused while a check or rewrite holds the piece. */
export const BUSY_WRITE = 'This piece is busy (a check or rewrite is running). Try again in a minute.'

/** Why Revise… is unavailable, or null when it can open. */
export function reviseBlockedReason(status: string, running: boolean, unsaved: boolean, canRevise: (s: string) => boolean): string | null {
  if (!canRevise(status)) return 'Only a piece under review or set aside can be revised.'
  if (running) return 'A piece is being written for this title. Revise when it finishes.'
  if (unsaved) return SAVE_FIRST
  return null
}

/** The draft-now status is checking facts (a recheck runs under the same job). */
export const isFactChecking = (st: { running?: boolean; stage?: string | null } | undefined): boolean =>
  !!st?.running && st.stage === 'fact-check'

type RunStatus = { running?: boolean; stage?: string | null; draft_id?: number | null; kind?: string | null } | undefined

/** A "Check facts again" is running for this very piece: the page waits read-only until it ends. */
export const isRecheckingPiece = (st: RunStatus, id: number): boolean =>
  !!st?.running && st.draft_id === id && st.kind !== 'revise' && (st.kind === 'recheck' || st.stage === 'fact-check')

/** A Revise of this very piece is running: the page waits read-only, then moves to the new version.
 *  The run's kind, not its stage: a revise goes on through writing, fact-check, photo and saving. */
export const isRevisingPiece = (st: RunStatus, id: number): boolean =>
  !!st?.running && st.draft_id === id && (st.kind === 'revise' || st.stage === 'revising')

/** What holds this piece read-only right now, or null when nothing does. */
export function pieceLock(st: RunStatus, id: number): 'revise' | 'recheck' | null {
  if (isRevisingPiece(st, id)) return 'revise'
  if (isRecheckingPiece(st, id)) return 'recheck'
  return null
}

/** Body, title, fields, Save and Publish all wait while a revise or a fact recheck holds the piece. */
export const isLocked = (st: RunStatus, id: number): boolean => pieceLock(st, id) !== null

export const REVISE_NOTE = 'Revising… you can edit again when it finishes.'

/** Why Publish… waits while the piece is held. */
export const REVISE_WAIT = 'Wait for the revise or fact check to finish.'

export const RECHECK_NOTE = 'Checking facts — editing resumes in about a minute'

/** The photo has a photograph but nobody could be named for it. */
export const missingCredit = (imageUrl: string | null | undefined, source: string | null | undefined): boolean =>
  !!imageUrl && (!source || source === 'uncredited' || source === 'none')

export const SUMMARY_UNAVAILABLE = 'The summary could not be written just now. Try again in a minute.'

/** The words under Write the summary when it fails: a 503 is the model being unavailable. */
export function summaryErrorText(err: unknown): string {
  return err instanceof ApiError && err.status === 503 ? SUMMARY_UNAVAILABLE : describeError(err)
}

/** Which fields the page may save: everything while the piece is open for edits (the destination
 *  even while its sources wait), only the Flight Line summary once a Both piece is live, and
 *  nothing on a taken-down piece or an earlier version. */
export function writableFields(status: string, destination: string): 'all' | 'summary' | 'none' {
  if (status === 'superseded' || status === 'unpublished') return 'none'
  if (status === 'published') return destination === 'both' ? 'summary' : 'none'
  return 'all'
}

/** Revise… and Check facts again run the writing agent, which refuses an imported piece
 *  (grounding 'import': no research or sources to rewrite or check against), so they are hidden there.
 *  An edit draft of a live article (origin 'edit') is grounded the same way. */
export const offersAgentActions = (origin: string | null | undefined): boolean => origin !== 'import' && origin !== 'edit'

/** The subtitle that opens the text: its first block when that is an <h3> (imports set the print deck
 *  that way). Publishing prints the byline right under it (wp_publish.place_byline), so the byline box says so. */
export function leadingSubtitle(html: string | null | undefined): string | null {
  const m = /^\s*<h3\b[^>]*>([\s\S]*?)<\/h3\s*>/i.exec(html ?? '')
  if (!m) return null
  const text = decodeEntities(m[1].replace(/<[^>]*>/g, '')).replace(/\s+/g, ' ').trim()
  return text || null
}

/** The warning on the Revise form: the rewrite is text only, so photos placed in the body
 *  (`<figure data-asset=`) are dropped. Null when the body has none (a video embed doesn't count). */
export function reviseFigureNote(bodyHtml: string | null | undefined): string | null {
  const n = (bodyHtml ?? '').match(/<figure\s[^>]*?data-asset=/gi)?.length ?? 0
  if (n === 0) return null
  return n === 1
    ? 'This piece has 1 photo in its body. Revise rewrites the text without it, so put it back after.'
    : `This piece has ${n} photos in its body. Revise rewrites the text without them, so put them back after.`
}
